import { Medal, Award, Star, Trophy, Diamond, User } from 'lucide-react';
import { useGamificationStore } from '../../stores/gamification-store';
import { useAuthStore } from '../../stores/auth-store';
import { useLanguage } from '../../contexts/LanguageContext';

interface ProfileBadgeProps {
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function ProfileBadge({ onClick, size = 'md', className = '' }: ProfileBadgeProps) {
  const { isAuthenticated } = useAuthStore();
  const { level, getLevelIcon } = useGamificationStore();
  const { t } = useLanguage();

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-8 h-8',
  };

  const levelBadgeSizes = {
    sm: 'w-4 h-4 text-[8px]',
    md: 'w-5 h-5 text-[10px]',
    lg: 'w-7 h-7 text-xs',
  };

  const levelBadgePositions = {
    sm: '-bottom-0.5 -right-0.5',
    md: '-bottom-1 -right-1',
    lg: '-bottom-1 -right-1',
  };

  // Get the appropriate icon based on level
  const getLevelIconComponent = () => {
    if (!isAuthenticated) {
      return <User className={iconSizes[size]} />;
    }

    const iconType = getLevelIcon();
    const iconClass = iconSizes[size];

    switch (iconType) {
      case 'diamond':
        return <Diamond className={iconClass} />;
      case 'trophy':
        return <Trophy className={iconClass} />;
      case 'star':
        return <Star className={iconClass} />;
      case 'award':
        return <Award className={iconClass} />;
      default:
        return <Medal className={iconClass} />;
    }
  };

  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center justify-center
        ${sizeClasses[size]}
        rounded-full
        ${isAuthenticated
          ? 'bg-primary-600 hover:bg-primary-700 shadow-md hover:shadow-lg'
          : 'bg-gray-100 hover:bg-gray-200 border border-gray-300'
        }
        text-white
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${className}
      `}
      aria-label={t('profileBadge.openProfileMenu', 'Open profile menu')}
      aria-haspopup="true"
    >
      <span className={isAuthenticated ? 'text-white' : 'text-gray-600'}>
        {getLevelIconComponent()}
      </span>

      {/* Level number badge - only show if authenticated and level > 0 */}
      {isAuthenticated && level > 0 && (
        <span
          className={`
            absolute ${levelBadgePositions[size]}
            ${levelBadgeSizes[size]}
            flex items-center justify-center
            bg-yellow-400 text-yellow-900 font-bold
            rounded-full
            border-2 border-white
            shadow-sm
          `}
        >
          {level}
        </span>
      )}
    </button>
  );
}
